import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import SolidGauge from 'highcharts/modules/solid-gauge';
import { useTranslation } from 'react-i18next';
import colors from 'theme/_colors.scss';
import { GaugeColors, GaugeDefaults } from './constants';
import './style.scss';

if (typeof Highcharts === 'object') {
    HighchartsMore(Highcharts);
    SolidGauge(Highcharts);
}

const getGaugeColor = (value, max) => {
    const percentage = value / max * 100;
    if (percentage <= 5) {
        return GaugeColors.Bad;
    } else if (percentage <= 30) {
        return GaugeColors.Tolerable;
    } else {
        return GaugeColors.Good;
    }
};

const useChartConfig = ({ value, min, max, unit }) => {
    const { t: translate } = useTranslation();
    const gaugeRef = useRef(null);
    const chartRef = useRef(null);

    const gaugeMin = min === null || min === undefined ? GaugeDefaults.MIN : min;
    const gaugeMax = max === null || max === undefined ? GaugeDefaults.MAX : max;
    const gaugeUnit = unit || GaugeDefaults.UNIT;
    const gaugeValue = value === null || value === undefined ? null : value;
    const isUnknown = gaugeValue === null;

    const displayValue = isUnknown
        ? translate('ABB.Powertrain.Frontend.unknownLabel')
        : `${GaugeDefaults.VALUE_PREFIX}${gaugeValue}${gaugeUnit}`;

    useEffect(() => {
        if (!gaugeRef.current) {
            return;
        }

        const color = isUnknown ? GaugeColors.Unknown : getGaugeColor(gaugeValue, gaugeMax);
        const plotValue = isUnknown ? 0 : Math.max(gaugeMin, Math.min(gaugeValue, gaugeMax));

        const config = {
            chart: {
                type: 'solidgauge',
                backgroundColor: 'transparent',
                spacing: [4, 4, 4, 4],
                height: null,
            },
            title: { text: null },
            pane: {
                center: ['58%', '78%'],
                size: '150%',
                startAngle: -90,
                endAngle: 90,
                background: {
                    backgroundColor: colors.surfaceSecondarySubtle,
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    borderWidth: 1,
                    borderRadius: '50%',
                    innerRadius: '60%',
                    outerRadius: '100%',
                    shape: 'arc'
                }
            },
            exporting: { enabled: false },
            tooltip: { enabled: false },
            yAxis: {
                min: gaugeMin,
                max: gaugeMax,
                stops: [[1, color]],
                lineWidth: 0,
                tickWidth: 0,
                minorTickInterval: null,
                tickAmount: 3,
                tickPositions: isUnknown ? [] : [gaugeMin, gaugeMax],
                labels: {
                    distance: -15,
                    textAlign: 'center',
                    y: 18,
                    style: {
                        color: colors.deionizerBlack,
                        fontSize: '12px',
                        fontWeight: '400',
                        lineHeight: '16px',
                    },
                    format: `{value}${gaugeUnit}`
                },
            },
            plotOptions: {
                solidgauge: {
                    innerRadius: '60%',
                    outerRadius: '100%',
                    rounded: false,
                    borderRadius: 4,
                    dataLabels: {
                        y: -45,
                        borderWidth: 0
                    }
                }
            },
            credits: { enabled: false },
            series: [{
                name: 'RUL',
                data: [plotValue],
                dataLabels: {
                    borderWidth: 0,
                    useHTML: true,
                    format: isUnknown
                        ? '<div class="rul-gauge-chart__value-text">?</div>'
                        : `<div class="rul-gauge-chart__value-text">${displayValue}</div>`
                },
            }],
        };

        chartRef.current = Highcharts.chart(gaugeRef.current, config);

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [gaugeValue, gaugeMin, gaugeMax, gaugeUnit, displayValue, isUnknown]);

    return (
        <div className='rul-gauge-chart' id='rul-gauge-chart'>
            <div ref={gaugeRef} className='rul-gauge-chart__container' id='rul-gauge-chart__container' />
        </div>
    );
};

useChartConfig.propTypes = {
    value: PropTypes.number,
    min: PropTypes.number,
    max: PropTypes.number,
    unit: PropTypes.string,
};

export default useChartConfig;
